import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const http = axios.create({
  baseURL: API,
  withCredentials: true,
});

// also include Bearer token (backup for environments where cookies are stripped)
http.interceptors.request.use((config) => {
  const t = localStorage.getItem("liq_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Global 403 EMAIL_NOT_VERIFIED handler — push the user to the "check inbox"
// screen so they get a clear next action instead of a generic error toast.
// Skipped for the verify/resend endpoints themselves to avoid loops.
http.interceptors.response.use(
  (resp) => resp,
  (err) => {
    const status = err?.response?.status;
    // Normalize structured error payloads to a display string BEFORE any caller
    // reads err.response.data.detail — prevents "[object Object]" toasts from
    // legacy call sites that concatenate detail directly. The original object
    // is preserved on `detail_raw` so callers that need the `code` can read it.
    if (
      err?.response?.data?.detail &&
      typeof err.response.data.detail === "object"
    ) {
      err.response.data.detail_raw = err.response.data.detail;
      err.response.data.detail = formatApiErrorDetail(err.response.data.detail);
    }
    const detail = err?.response?.data?.detail_raw ?? err?.response?.data?.detail;
    const url = err?.config?.url || "";
    if (
      status === 403 &&
      detail === "EMAIL_NOT_VERIFIED" &&
      !url.includes("/auth/verify-email") &&
      !url.includes("/auth/resend-verification") &&
      !url.includes("/auth/me") &&
      !url.includes("/auth/logout") &&
      window.location.pathname !== "/check-inbox" &&
      window.location.pathname !== "/verify-email"
    ) {
      window.location.assign("/check-inbox");
    }
    // Global "billing profile required" handler — before ANY payment we require
    // a complete Bill-To profile so the invoice is legally valid. Redirect the
    // user to /settings/billing with a `?next=` param so they can come back and
    // click Buy again after saving.
    if (
      status === 400 &&
      detail &&
      typeof detail === "object" &&
      detail.code === "billing_profile_required" &&
      window.location.pathname !== "/settings/billing"
    ) {
      toast.error(
        detail.message ||
          "Complete your billing details before making a payment."
      );
      const next = encodeURIComponent(
        window.location.pathname + window.location.search
      );
      // Small delay so the toast is visible during route change.
      setTimeout(() => {
        window.location.assign(`/settings/billing?next=${next}&reason=payment`);
      }, 250);
    }
    return Promise.reject(err);
  },
);

export function formatApiErrorDetail(detail) {
  // Never let a caller show "[object Object]" — always fall back to a readable string.
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : formatApiErrorDetail(e)))
      .filter(Boolean)
      .join(" ");
  if (typeof detail === "object") {
    // FastAPI/Pydantic validation error shape
    if (typeof detail.msg === "string") return detail.msg;
    // Structured error envelope (code + message) — used for billing_profile_required, etc.
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.detail === "string") return detail.detail;
    if (typeof detail.error === "string") return detail.error;
    // Last resort: serialize but don't leak "[object Object]"
    try {
      return JSON.stringify(detail);
    } catch {
      return "Something went wrong. Please try again.";
    }
  }
  return String(detail);
}
